var _PMI;

/**
 * An enumeration of loan types. Available loan types are `FIXED` and `ARM`.
 *
 * @property LOAN_TYPE
 * @static
 */
var LOAN_TYPE = {
  FIXED: 'fixed',
  ARM: 'arm'
};
/**
 * A table of LTV ratios to annual PMI rates by loan type. For example, the
 * PMI rate for a fixed loan with an 90% LTV would be:
 *
 *     var pmiRate = PMI[LOAN_TYPE.FIXED][90];
 *
 * @property PMI
 * @static
 */

var PMI = (_PMI = {}, _PMI[LOAN_TYPE.FIXED] = {
  80: 0,
  85: 0.0044,
  90: 0.0059,
  95: 0.0076,
  100: 0.0098
}, _PMI[LOAN_TYPE.ARM] = {
  80: 0,
  85: 0.0068,
  90: 0.0081,
  95: 0.0101,
  100: 0.0117
}, _PMI);
/**
 * Get the PMI bucket for the `ZMM.PMI` table.
 *
 * @method getPMIBucket
 * @static
 * @param ltv The loan-to-value ratio as a percentage, i.e. 95
 */

function getPMIBucket(ltv) {
  if (ltv > 95) {
    return 100;
  } else if (ltv > 90) {
    return 95;
  } else if (ltv > 85) {
    return 90;
  } else if (ltv > 80) {
    return 85;
  } else {
    return 80;
  }
}
/**
 * Calculates and returns the estimated monthly loan payment for the given
 * loan amount, percentage interest rate, and loan term in months.  The
 * equation used to calculate the monthly payment is:
 *
 *                  (LOAN_AMOUNT) * MONTHLY_RATE * (1 + MONTHLY_RATE)^TERM
 *     MONTHLY_PI = ------------------------------------------------------
 *                              (1 + MONTHLY_RATE)^TERM - 1
 *
 * @method monthlyPI
 * @static
 * @param {Number} amount The total loan amount
 * @param {Number} rate The interest rate of the loan as a percent
 * @param {Number} term The loan term in months
 * @param {boolean} [noRounding=false] Do not round the result (the default
 *     behavior is to round to a dollar value with two decimal places)
 * @return {Number} the monthly principal and interest payment
 */

function monthlyPI(amount, rate, term, noRounding) {
  // No interest, split amount by the number of terms
  if (rate === 0) {
    return $$(amount / term);
  } // Over 12 months converted to decimal


  var monthlyRate = rate / 1200;
  var exponentiationTerm = Math.pow(1 + monthlyRate, term);
  var monthlyPIAmount = amount * monthlyRate * exponentiationTerm / (exponentiationTerm - 1); // Return rounded dollar value

  if (!noRounding) {
    return $$(monthlyPIAmount);
  }

  return monthlyPIAmount;
}
function loanAmount(monthlyPIAmount, rate, term) {
  if (rate === 0) {
    return 0; // This is impossible
  }

  var monthlyRate = rate / 1200;
  var exponentiationTerm = Math.pow(1 + monthlyRate, term);
  return monthlyPIAmount / (monthlyRate * exponentiationTerm / (exponentiationTerm - 1));
}
/**
 * Calculates and returns the estimated monthly property taxes given
 * the property value and tax rate.
 * The equation used to calculate the monthly property tax is:
 *
 *                             (PROPERTY_VALUE) * TAX_RATE
 *    MONTHLY_PROPERTY_TAXES = ----------------------------
 *                                          12
 *
 * @method monthlyPropertyTaxes
 * @static
 * @param {Number} property value
 * @param {Number} taxRate
 * @return {Number} the monthly property tax
 */

function monthlyPropertyTaxes(propertyValue, taxRate) {
  return propertyValue * taxRate / 12;
}
/**
 * Calculates and returns the estimated monthly PMI given the
 * loan amount, property value, and whether the loan ARM.
 * The equation used to calculate the monthly PMI is:
 *
 *                   (PMI) * LOAN_AMOUNT
 *     MONTHLY_PMI = --------------------
 *                           12
 *
 * @method monthlyPMI
 * @static
 * @param {Number} loanAmountValue
 * @param {Number} propertyValue
 * @param {boolean} isARM
 * @return {Number} the monthly PMI
 */

function monthlyPMI(loanAmountValue, propertyValue, isARM) {
  var ltv = loanAmountValue / propertyValue * 100;
  var pmiRate = PMI[isARM ? LOAN_TYPE.ARM : LOAN_TYPE.FIXED][getPMIBucket(ltv)];
  return pmiRate * loanAmountValue / 12;
}
/**
 * Calculate the mortgage amount based on monthly principal and interest
 * payment.
 *
 * - `n` Number of Payments
 * - `i` Monthly Interest Rate
 * - `pi` Monthly Principal and Interest
 *
 * `mortgageAmount = pi * [(1 + i)^n – 1] / [i * (1 + i)^n]`
 *
 * @method amountByPayment
 * @static
 * @param {number} monthlyPIAmount The monthly principal and interest payment
 * @param {number} rate The interest rate of the loan as a percent
 * @param {number} term The loan term in months
 * @param {boolean} [noRounding=false] Do not round the result (the default
 *     behavior is to round to a dollar value with two decimal places)
 * @return {number} The total loan amount
 */

function amountByPayment(monthlyPIAmount, rate, term, noRounding) {
  var monthlyRate;
  var exponentiationTerm;
  var amount;

  if (rate > 0) {
    // Over 12 months converted to decimal
    monthlyRate = rate / 1200;
    exponentiationTerm = Math.pow(1 + monthlyRate, term);
    amount = monthlyPIAmount * (exponentiationTerm - 1) / (monthlyRate * exponentiationTerm);
  } else {
    // No interest, full payment goes towards amount
    amount = term * monthlyPIAmount;
  } // Return rounded dollar value


  if (!noRounding) {
    return $$(amount);
  }

  return amount;
}
/**
 * @method amountByLTV
 * @static
 * @param {number} downPayment The down payment amount.
 * @param {number} ltv Loan to value as a percent, i.e. 80
 */

function amountByLTV(downPayment, ltv) {
  return $$(downPayment * (1 / (1 - ltv / 100)));
}
/**
 * Get the amortization schedule for the given loan parameters.
 *
 * @method amortization
 * @static
 * @param {Number} amount The total loan amount
 * @param {Number} rate The interest rate of the loan as a percent
 * @param {Number} term The loan term in months
 * @return {Object} The amortization schedule
 */

function amortization(amount, rate, term) {
  var monthlyRate = rate / 1200; // Over 12 months converted to decimal

  var monthlyPIAmount = monthlyPI(amount, rate, term);
  var remainingBalance = amount;
  var totalInterest = 0;
  var amortizationTable = [];
  var i;
  var monthlyInterest;
  var monthlyPrincipal;
  var twelveMonthsInterest = 0;
  var twelveMonthsPrincipal = 0;

  for (i = 0; i < term; i += 1) {
    // Interest
    monthlyInterest = $$(remainingBalance * monthlyRate);
    totalInterest = $$(totalInterest + monthlyInterest); // Principal

    if (i === term - 1) {
      // The last payment makes up for any rouding differences
      monthlyPIAmount = $$(remainingBalance + monthlyInterest);
      monthlyPrincipal = remainingBalance;
      remainingBalance = 0;
    } else {
      monthlyPrincipal = $$(monthlyPIAmount - monthlyInterest);
      remainingBalance = $$(remainingBalance - monthlyPrincipal);
    }

    twelveMonthsInterest += monthlyInterest;
    twelveMonthsPrincipal += monthlyPrincipal;

    if (i - 12 >= 0) {
      twelveMonthsInterest -= amortizationTable[i - 12].monthlyInterest;
      twelveMonthsPrincipal -= amortizationTable[i - 12].monthlyPrincipal;
    }

    amortizationTable.push({
      month: i + 1,
      monthlyInterest: monthlyInterest,
      totalInterest: totalInterest,
      monthlyPrincipal: monthlyPrincipal,
      totalPrincipal: $$(amount - remainingBalance),
      remainingBalance: remainingBalance,
      twelveMonthsInterest: twelveMonthsInterest,
      twelveMonthsPrincipal: twelveMonthsPrincipal
    });
  }

  return amortizationTable;
}
/**
 * Get the number of payments remaining in the loan.
 *
 * @method paymentsRemaining
 * @static
 * @param {Number} originationYear The year the loan originated
 * @param {Number} term The loan term in months
 * @return {Number} The number of payments remaining
 */

function paymentsRemaining(originationYear, term) {
  var today = new Date();
  var yearsPaid = today.getFullYear() - originationYear;
  var monthsPaid = today.getMonth() + 1; // Assume July 1st origination month (unless originating in the same year).

  var paymentsMade = Math.max(yearsPaid * 12 - 6, 0) + monthsPaid;
  var remaining = term - paymentsMade; // Future origination date should have all payments still

  if (yearsPaid < 0) {
    remaining = term;
  } // Can't have negative payments remaining


  if (remaining < 0) {
    remaining = 0;
  }

  return remaining;
}
/**
 * Round to two decimal points. This is useful for fixing floating point
 * precision issues.
 *
 * @method $$
 * @private
 * @param {Number} amount The amount to round
 * @return {Number} The amount rounded to two decimal places
 */

function $$(amount) {
  return +amount.toFixed(2);
}
/**
 * Get default inputs for payment calculations. Optionally pass in an
 * object of default overrides.
 *
 * @method paymentDefaults
 * @param {Object} overrides
 */

function paymentDefaults(overrides) {
  return Object.assign({
    // The price of the home.
    homePrice: 300000,
    // The down payment amount.
    downPayment: 60000,
    // The loan term in months.
    term: 360,
    // The percentage of interest associated with the loan
    rate: 4.176,
    // Current fixed 30-year rate
    rate30Year: 4.176,
    // Current fixed 15-year rate
    rate15Year: 3.805,
    // Current 5/1 ARM rate
    rateARM5: 3.905,
    // The annual property tax rate as a percentage.
    propertyTaxRate: 1.2,
    // Include property tax in computation?
    includePropertyTax: true,
    // The annual cost of homeowners insurance.
    annualHomeownersInsurance: 800,
    // Include homeowners insurance in computation?
    includeHomeownersInsurance: true,
    // Include private mortgage insurance in computation?
    includePMI: true,
    // The monthly homeowners association dues.
    monthlyHOA: 0,
    // Include homeowners association dues in computation?
    includeHOA: true,
    // Is the loan program an ARM? This is used to calculate the PMI only.
    isARM: false,
    // The debt-to-income ration as a percent. This is used only to
    // calculated the estimated required income.
    dti: 36
  }, overrides);
}
/**
 * A monthly payment calculator.
 *
 * @method payment
 */

function payment(_ref) {
  var term = _ref.term,
      homePrice = _ref.homePrice,
      rate = _ref.rate,
      downPayment = _ref.downPayment,
      includeHOA = _ref.includeHOA,
      monthlyHOA = _ref.monthlyHOA,
      includeHomeownersInsurance = _ref.includeHomeownersInsurance,
      annualHomeownersInsurance = _ref.annualHomeownersInsurance,
      includePropertyTax = _ref.includePropertyTax,
      propertyTaxAmount = _ref.propertyTaxAmount,
      propertyTaxRate = _ref.propertyTaxRate,
      includePMI = _ref.includePMI,
      includeTaxesInsurance = _ref.includeTaxesInsurance,
      isARM = _ref.isARM,
      dti = _ref.dti;
  var loanAmountValue = homePrice - downPayment;
  var piPayment = monthlyPI(loanAmountValue, rate, term);
  var hoa = includeHOA ? monthlyHOA : 0;
  var homeIns = includeHomeownersInsurance ? annualHomeownersInsurance / 12 : 0;
  var propTax = includePropertyTax ? propertyTaxAmount ? propertyTaxAmount / 12 : monthlyPropertyTaxes(homePrice, propertyTaxRate / 100) : 0;
  var pmi = includePMI && includeTaxesInsurance ? monthlyPMI(loanAmountValue, homePrice, isARM) : 0;
  var monthlyTaxesAndInsurance = propTax + homeIns + pmi + hoa;
  var monthlyPaymentAmount = piPayment + (monthlyTaxesAndInsurance || 0);
  var income = monthlyPaymentAmount / dti / 100 * 12;
  return {
    monthlyPayment: monthlyPaymentAmount || 0,
    monthlyPI: piPayment || 0,
    monthlyPropertyTax: propTax || 0,
    monthlyHomeownersInsurance: homeIns || 0,
    monthlyPMI: homePrice === 0 ? 0 : pmi || 0,
    requiredIncome: income || 0,
    monthlyHOA: hoa || 0,
    amortization: amortization(loanAmountValue, rate, term)
  };
}

function calculateLoanAmount(_ref) {
  var originationYear = _ref.originationYear,
      rate = _ref.rate,
      term = _ref.term,
      currentBalance = _ref.currentBalance;
  var today = new Date();
  var yearsPaid = today.getFullYear() - originationYear;
  var monthsPaid = today.getMonth() + 1;
  var paymentsMade = Math.max(yearsPaid * 12 - 6, 0) + monthsPaid;
  var remainingTerm = term - paymentsMade;
  var monthlyPI$1 = monthlyPI(currentBalance, rate, remainingTerm);
  return loanAmount(monthlyPI$1, rate, term);
}
/**
 * David Cantrell's interest rate estimation formula
 * https://brownmath.com/bsci/loan.htm#Cantrell2
 *
 * @param loanAmount
 * @param term (in months)
 * @param monthlyPayment
 * @param paymentPerYear
 * @returns {Number} estimated interest rate as a percent (0 - 100)
 */

function estimateRate(_ref2) {
  var loanAmount = _ref2.loanAmount,
      term = _ref2.term,
      monthlyPayment = _ref2.monthlyPayment;
  var totalPayments = term;
  var q = Math.log(1 + 1 / totalPayments) / Math.log(2);
  return 100 * 12 * (Math.pow(Math.pow(1 + monthlyPayment / loanAmount, 1 / q) - 1, q) - 1);
}
function estimateRateForDesiredSavings(_ref3) {
  var loanAmount = _ref3.loanAmount,
      term = _ref3.term,
      currentRate = _ref3.currentRate,
      currentBalance = _ref3.currentBalance,
      desiredSavings = _ref3.desiredSavings,
      _ref3$newTerm = _ref3.newTerm,
      newTerm = _ref3$newTerm === void 0 ? 360 : _ref3$newTerm;
  var currentPayment = monthlyPI(loanAmount, currentRate, term);
  var desiredNewPayment = currentPayment - desiredSavings;
  return estimateRate({
    loanAmount: currentBalance,
    term: newTerm,
    monthlyPayment: desiredNewPayment
  });
}
function calculateSavings(_ref4) {
  var loanAmount = _ref4.loanAmount,
      term = _ref4.term,
      currentRate = _ref4.currentRate,
      currentBalance = _ref4.currentBalance,
      _ref4$newTerm = _ref4.newTerm,
      newTerm = _ref4$newTerm === void 0 ? 360 : _ref4$newTerm,
      newRate = _ref4.newRate;
  var currentPayment = monthlyPI(loanAmount, currentRate, term);
  var newPayment = monthlyPI(currentBalance, newRate, newTerm);
  return currentPayment - newPayment;
}
/**
 *  Get the length of the term for a loan based on the monthlyPayment.
 *
 *   FORMULA:
 *              (        MONTHLY_PI                  )
 LOG(      ---------------               )
 *              (MONTHLY_PI - LOAN_AMT * MONTHLY_RATE)
 *   TERM = -------------------------------------------
 *                   LOG(1 + MONTHLY_RATE)
 */

function calculateNewTermFromPayment(loanAmount, rate, monthlyPI) {
  var monthlyRate = rate / 1200;
  var top = Math.log(monthlyPI / (monthlyPI - loanAmount * monthlyRate));
  var bottom = Math.log(1 + monthlyRate);
  var result = top / bottom;
  return Math.round(result);
}
/**
 * Number of payments remaining on a current balance
 *
 * N = −log(1−iA/P) / log(1+i)
 * where i = rate, A = currentBalance, P = monthlyPI
 */

function paymentsRemaining$1(_ref5) {
  var loanAmount = _ref5.loanAmount,
      currentBalance = _ref5.currentBalance,
      rate = _ref5.rate,
      term = _ref5.term;
  var monthlyPI$1 = monthlyPI(loanAmount, rate, term);
  return calculateNewTermFromPayment(currentBalance, rate, monthlyPI$1);
}
var calculateNewTermFromAdditionalPayment = function calculateNewTermFromAdditionalPayment(_ref6) {
  var loanAmount = _ref6.loanAmount,
      currentBalance = _ref6.currentBalance,
      rate = _ref6.rate,
      term = _ref6.term,
      additionalPayment = _ref6.additionalPayment;
  var monthlyPI$1 = monthlyPI(loanAmount, rate, term);
  return calculateNewTermFromPayment(currentBalance, rate, monthlyPI$1 + additionalPayment);
};
var calculateAdditionalPaymentFromNewTerm = function calculateAdditionalPaymentFromNewTerm(_ref7) {
  var loanAmount = _ref7.loanAmount,
      currentBalance = _ref7.currentBalance,
      rate = _ref7.rate,
      term = _ref7.term,
      newTerm = _ref7.newTerm;
  var monthlyPI$1 = monthlyPI(loanAmount, rate, term);
  var newMonthlyPI = monthlyPI(currentBalance, rate, newTerm);
  return newMonthlyPI - monthlyPI$1;
};

function monthDiff(d1, d2) {
  var months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months -= d1.getMonth() + 1;
  months += d2.getMonth();
  return months <= 0 ? 0 : months;
}

function calculateRemainingBalance(initialLoanAmount, rate, loanTermInMonths, originationYear) {
  var originationYearDate = new Date(originationYear, 7, 1); //middle of the year since we don't know the exact date

  var currentDate = new Date();
  var amortization$1 = amortization(initialLoanAmount, rate, loanTermInMonths);
  var monthsDifference = monthDiff(originationYearDate, currentDate);

  if (monthsDifference <= 0) {
    return initialLoanAmount;
  }

  return amortization$1[monthsDifference - 1].remainingBalance;
}
function monthsUntilTargetRemainingBalance(_ref8) {
  var targetRemainingBalance = _ref8.targetRemainingBalance,
      loanAmount = _ref8.loanAmount,
      rate = _ref8.rate,
      term = _ref8.term;
  var amortization$1 = amortization(loanAmount, rate, term);

  for (var i = 0; i < amortization$1.length; i++) {
    if (amortization$1[i].remainingBalance <= targetRemainingBalance) {
      return amortization$1[i].month;
    }
  }

  return null;
}
/**
 * Calculate the results of refinancing
 *
 * @method affordability
 * inputs
 *  - loanAmount
 *  - rate
 *  - term (in months)
 *  - originationYear OR currentBalance
 *  - rollFees
 *  - fees
 *  - cashOut
 *  - newRate
 *  - newTerm
 *  - newLoanAmount
 */

function refinance (inputs) {
  var currentPayment = monthlyPI(inputs.loanAmount, inputs.rate, inputs.term);
  var currentRemaining = inputs.originationYear ? paymentsRemaining(inputs.originationYear, inputs.term) : paymentsRemaining$1({
    loanAmount: inputs.loanAmount,
    currentBalance: inputs.currentBalance,
    rate: inputs.rate,
    term: inputs.term
  }); // Are fees rolled into the new loan amount?

  var newTotal;
  var fees;

  if (inputs.rollFees) {
    newTotal = inputs.newLoanAmount + inputs.fees + inputs.cashOut;
    fees = 0;
  } else {
    newTotal = inputs.newLoanAmount + inputs.cashOut;
    fees = inputs.fees;
  } // Calculate new payment based on new calculated loan amount


  var newPayment = monthlyPI(newTotal, inputs.newRate, inputs.newTerm); // Calculate the new monthly savings amount

  var monthlySavings;

  if (currentRemaining === 0) {
    monthlySavings = $$(-newPayment);
  } else {
    monthlySavings = $$(currentPayment - newPayment);
  } // Calculate the new amortization schedule


  var schedule = amortization(newTotal, inputs.newRate, inputs.newTerm); // Get the number of payments for the new loan

  var newRemaining = schedule.length; // Savings initialized at the price to refinance

  var totalSavings = -fees;
  var previousTotal = totalSavings; // The number of months in the savings schedule may be larger than
  // the amortization table if you reduce the term of your loan

  var months = Math.max(newRemaining, currentRemaining); // Update amortization schedule with savings information

  var breakeven;

  for (var i = 0; i < months; i += 1) {
    // Make sure there is an object for this month (there won't be
    // an object for months greater than the amortization schedule).
    if (!schedule[i]) {
      schedule[i] = {
        month: i + 1
      };
    } // Caculate total savings


    if (i < currentRemaining && i < newRemaining) {
      // While both your previous and current loan are active,
      // your savings are the difference of the monthly payments
      totalSavings = $$(totalSavings + monthlySavings);
    } else if (i < currentRemaining && i >= newRemaining) {
      // Your new loan is paid off, you save the loan payment you
      // would have been paying on your own loan
      totalSavings = $$(totalSavings + currentPayment);
    } else {
      // Your old loan is paid off so your losing money with each
      // new payment
      totalSavings = $$(totalSavings - newPayment);
    }

    schedule[i].totalSavings = totalSavings; // Did you breakeven this term?

    if (previousTotal <= 0 && totalSavings >= 0) {
      schedule[i].breakEven = true;
      breakeven = i + 1;
    } // Did we go negative this term?


    if (previousTotal >= 0 && totalSavings < 0) {
      schedule[i].breakNegative = true;
    } // Would this have been the last payment on previous loan


    if (i === currentRemaining - 1) {
      schedule[i].currentLoanComplete = true;
    } // You would have still been making payments on previous loan


    if (i === newRemaining - 1) {
      schedule[i].newLoanComplete = true;
    } // Update previous


    previousTotal = totalSavings;
  }

  return {
    lifetimeSavings: totalSavings,
    breakEven: breakeven,
    currentPayment: currentPayment,
    newPayment: newPayment,
    newTotal: newTotal,
    monthlySavings: monthlySavings,
    savingsSchedule: schedule
  };
}

var _refinance = /*#__PURE__*/Object.freeze({
    __proto__: null,
    calculateLoanAmount: calculateLoanAmount,
    estimateRate: estimateRate,
    estimateRateForDesiredSavings: estimateRateForDesiredSavings,
    calculateSavings: calculateSavings,
    calculateNewTermFromPayment: calculateNewTermFromPayment,
    paymentsRemaining: paymentsRemaining$1,
    calculateNewTermFromAdditionalPayment: calculateNewTermFromAdditionalPayment,
    calculateAdditionalPaymentFromNewTerm: calculateAdditionalPaymentFromNewTerm,
    calculateRemainingBalance: calculateRemainingBalance,
    monthsUntilTargetRemainingBalance: monthsUntilTargetRemainingBalance,
    'default': refinance
});

function _isNumber(value) {
  return typeof value === 'number';
}
/**
 * Get the loan to value (LTV) of the total.
 *
 * @method _ltv
 * @private
 * @param {number} total Total loan amount
 * @param {number} downPayment
 * @return The LTV as a percent, i.e. 90 for 90%
 */


function _ltv(total, downPayment) {
  return (total - downPayment) / total * 100;
}
/**
 * Helper for getting the annual property tax. Annual taxes can be supplied by
 * rate or by amount.
 *
 * @method _annualPropertyTax
 * @private
 * @param {object} inputs Affordability inputs
 * @param {number} total Total loan amount
 * @return {number} The annual property tax amount.
 */


function _annualPropertyTax(inputs, total) {
  if (!inputs.includePropertyTax) {
    return 0;
  } else if (_isNumber(inputs.propertyTaxAmount)) {
    return inputs.propertyTaxAmount;
  } else {
    return total * inputs.propertyTaxRate / 100;
  }
}
/**
 * Calculate the affordability including an additional percentage rate.
 * The additional rate could be for taxes and/or pmi.
 *
 * @method _affordabilityWith
 * @private
 * @param {number} affordability The initial affordability calculated
 *     without PMI or taxes
 * @param {number} withRate The monthly percentage rate as a decimal,
 *     i.e. 0.02 for 2%
 */


function _affordabilityWith(affordability, withRate, rate, term) {
  var monthlyRate = rate / 1200;
  var exponentiationTerm = Math.pow(1 + monthlyRate, term);
  return affordability / (1 + withRate * (exponentiationTerm - 1) / (monthlyRate * exponentiationTerm));
}
/**
 * The inverse of _affordabilityWith. Used to calculate an initial total
 * without taxes or pmi included.
 *
 * @method _affordabilityWithout
 * @private
 * @param {number} affordabilityWith Affordability calculated by
 *     _affordabilityWith
 * @param {number} withRate The monthly percentage rate to back out,
 *     i.e. 0.02 for 2%
 */


function _affordabilityWithout(affordabilityWith, withRate, rate, term) {
  var monthlyRate = rate / 1200;
  var exponentiationTerm = Math.pow(1 + monthlyRate, term);
  return affordabilityWith * (1 + withRate * (exponentiationTerm - 1) / (monthlyRate * exponentiationTerm));
}
/**
 * Get default inputs for affordability calculations. Optionally pass in an
 * object of default overrides.
 *
 * @method affordabilityDefaults
 * @param {Object} overrides
 */


function affordabilityDefaults(overrides) {
  return Object.assign({
    // Gross annual income.
    annualIncome: 70000,
    // If `customPayment` is true, then use this monthly payment when
    // calculating affordability by payment.
    monthlyPayment: 1850,
    // When calculating affordability by payment, use the specified
    // `monthlyPayment` value rather than calculating a monthly payment
    // from `annualIncome` and `dti`.
    customPayment: false,
    // The total monthly debts. Include credit card payments, car loan
    // payments, student loan payments, child support payments, alimony
    // payments, and legal judgments. Don't include PITI payments.
    monthlyDebts: 0,
    // The down payment for the loan.
    downPayment: 20000,
    // The interest rate for the loan as a percentage, i.e. 3.5.
    rate: 3.875,
    // The term of the loan in months. For example, the term for a 30
    // year fixed would be 360.
    term: 360,
    // The annual property taxes as a dollar amount. If set, this takes
    // precedence over propertyTaxRate.
    propertyTaxAmount: undefined,
    // The annual property tax rate as a percentage, i.e. 1.5.
    propertyTaxRate: 0,
    // Include property taxes in the calculation.
    includePropertyTax: false,
    // Include private mortgage insurance (PMI) in the calculation.
    includePMI: true,
    // The total annual homeowners insurance premium. The second "I" in
    // "PITI". Don't include hazard insurance or mortgage insurance.
    annualHomeownersInsurance: 0,
    // Include homeowners insurance in the calculation?
    includeHomeownersInsurance: true,
    // The monthly homeowners association dues. This value should be
    // included with the other PITI values.
    monthlyHOA: 0,
    // Include homeowners association dues in calculation?
    includeHOA: true,
    // The loan-to-value ratio to use when calculating the affordability
    // by LTV.
    ltv: 80,
    // The loan-to-value ratio to use when calculating the affordability
    // by LTV for jumbo loans.
    ltvJumbo: 80,
    // The dollar amount to start using the jumbo ltv when calculating
    // affordability by ltv.
    jumboLimit: 453100,
    // The backend debt-to-income ratio (includes both PITI and other
    // debts) as a percentage, i.e. 36.
    dti: 36,
    // The loan type. This is used to get the PMI rate.
    loanType: LOAN_TYPE.FIXED
  }, overrides);
}
/**
 * Calculate the affordability by DTI.
 *
 * @method affordabilityByDTI
 */

function affordabilityByDTI(inputs) {
  var taxByAmount = _isNumber(inputs.propertyTaxAmount);

  var monthlyIncome;
  var monthlyInsurance = 0;
  var monthlyTaxes = 0;
  var monthlyHOA = 0;
  var monthlyAvailable;
  var total;
  var taxRate = 0;
  var pmiRate = 0;
  var pmiTotal;
  var pmiTotals = [];
  var pmiBucket;
  var ltv;
  var iBucket;
  var outputs = {};

  if (inputs.includeHomeownersInsurance) {
    monthlyInsurance = inputs.annualHomeownersInsurance / 12;
  }

  if (inputs.includeHOA) {
    monthlyHOA = inputs.monthlyHOA;
  }

  if (inputs.includePropertyTax && taxByAmount) {
    monthlyTaxes = inputs.propertyTaxAmount / 12;
  } // Get the monthly available amount, or calculate it from income if
  // not given.


  if (inputs.customPayment) {
    monthlyAvailable = inputs.monthlyPayment - monthlyInsurance - monthlyHOA - monthlyTaxes;
  } else {
    monthlyIncome = inputs.annualIncome / 12; // Monthly available is the monthly income multiplied by the
    // dti ratio minus any other monthly debts (not including PMI).

    monthlyAvailable = monthlyIncome * inputs.dti / 100 - monthlyInsurance - monthlyHOA - monthlyTaxes - inputs.monthlyDebts;
  } // No money for monthly payments


  if (monthlyAvailable <= 0) {
    total = inputs.downPayment;
    outputs.ltv = 0;
    outputs.affordabilityByDTI = total;
    outputs.annualPropertyTax = $$(_annualPropertyTax(inputs, total));
    return outputs;
  } // The initial total is calculated without taxes or PMI. The
  // total is the sum of the loan amount calculated from the
  // payment, plus the down payment.


  var initialTotal = amountByPayment(monthlyAvailable, inputs.rate, inputs.term, true) + inputs.downPayment;
  total = initialTotal; // Update the amount with property taxes included

  if (inputs.includePropertyTax && !taxByAmount) {
    taxRate = inputs.propertyTaxRate / 1200;
    total = _affordabilityWith(initialTotal, taxRate, inputs.rate, inputs.term);
  } // Update the amount with PMI included


  if (inputs.includePMI) {
    // Calculate ltv for PMI bucket
    ltv = _ltv(total, inputs.downPayment);
    pmiBucket = getPMIBucket(ltv);
    pmiRate = PMI[inputs.loanType][pmiBucket] / 12;
    pmiTotals.push(_affordabilityWith(initialTotal, pmiRate + taxRate, inputs.rate, inputs.term)); // Test other PMI buckets as they will sometimes result in an
    // overally higher affordability.

    for (iBucket = 80; iBucket < pmiBucket; iBucket += 5) {
      // Calculate affordability needed for a this PMI bucket
      pmiTotal = amountByLTV(inputs.downPayment, iBucket); // Update total to not include tax rate

      pmiTotal = _affordabilityWithout(pmiTotal, taxRate, inputs.rate, inputs.term); // Calculate affordability with the new PMI rate

      pmiRate = PMI[inputs.loanType][iBucket] / 12;
      pmiTotals.push(_affordabilityWith(pmiTotal, pmiRate + taxRate, inputs.rate, inputs.term));
    }

    total = Math.max.apply(Math, pmiTotals);
  } // Affordability with taxes/insurance can be less than down payment
  // for a really large down payment, take the max of the two


  total = Math.max(total, inputs.downPayment); // Update read only attributes

  outputs.ltv = _ltv(total, inputs.downPayment);
  outputs.affordabilityByDTI = $$(total);
  outputs.annualPropertyTax = $$(_annualPropertyTax(inputs, total));
  return outputs;
}
/**
 * Calculate the affordability by LTV
 *
 * @method affordabilityByLTV
 */

function affordabilityByLTV(inputs) {
  var affordability = amountByLTV(inputs.downPayment, inputs.ltv);
  var loanAmount = affordability - inputs.downPayment;

  if (loanAmount > inputs.jumboLimit) {
    affordability = Math.max(inputs.jumboLimit + inputs.downPayment, amountByLTV(inputs.downPayment, inputs.ltvJumbo));
    loanAmount = affordability - inputs.downPayment;
  }

  return {
    affordabilityByLTV: affordability,
    loanAmountByLTV: loanAmount
  };
}
/**
 * Calculate the overall affordability, comparing affordability by LTV
 * and affordability by DTI.
 *
 * @method affordability
 */

function calculateOverallAffordability(inputs) {
  var byDTI = affordabilityByDTI(inputs);
  var byLTV = affordabilityByLTV(inputs);
  var affordability = Math.min(byDTI.affordabilityByDTI, byLTV.affordabilityByLTV);
  var loanAmount = affordability - inputs.downPayment;
  return Object.assign(byDTI, byLTV, {
    affordability: affordability,
    loanAmount: loanAmount
  });
}

var _affordability = /*#__PURE__*/Object.freeze({
    __proto__: null,
    affordabilityDefaults: affordabilityDefaults,
    affordabilityByDTI: affordabilityByDTI,
    affordabilityByLTV: affordabilityByLTV,
    'default': calculateOverallAffordability
});

var refinance$1 = _refinance;
var affordability = _affordability;
var mortgageCalculations = payment;

export default mortgageCalculations;
export { $$, LOAN_TYPE, PMI, affordability, amortization, amountByLTV, amountByPayment, getPMIBucket, loanAmount, monthlyPI, monthlyPMI, monthlyPropertyTaxes, paymentDefaults, paymentsRemaining, refinance$1 as refinance };
